"use client";

import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import { Popover, PopoverTrigger, PopoverContent } from "../../ui/popover";
import { STATUS_COLORS } from "../../../constants/colors";

import ShadcnProjectSelector from "./components/ShadcnProjectSelector";
import SuggestionCarousel from "./components/SuggestionCarousel";
import { ChatContainer, ChatForm } from "@/components/ui/chat";
import { MessageInput } from "@/components/ui/message-input";

// Move suggestions outside component to prevent recreation on every render
const SUGGESTIONS = [
  {
    title: "Cloud SQL",
    description:
      "Create PostgreSQL Cloud SQL instance in us-central1, HA enabled",
    icon: "cloud-sql",
  },
  {
    title: "Kubernetes cluster",
    description:
      "Create GKE cluster in us-central1, 3 nodes, autoscaling enabled, use the default configuration",
    icon: "kubernetes",
  },
  {
    title: "Storage bucket",
    description:
      "I need a storage bucket with the name 'leapfrog-bucket' in my project aluna-workspace with uniform access and versioning enabled in us-central1.",
    icon: "storage",
  },
  {
    title: "Launch a VM machine",
    description:
      "Create a VM named test-vm in the us-central1-a zone with machine type e2-medium, boot disk image debian-cloud/debian-11, and network interface default, all under the project leapfrog-workspace.",
    icon: "vm",
  },
  {
    title: "VPC network",
    description:
      "I need a VPC with the name 'leapfrog-vpc' in my project aluna-workspace with two subnets in us-central1.",
    icon: "vpc",
  },
] as const;

const AILandingPage: React.FC = () => {
  const [input, setInput] = useState("");
  const [selectedProject, setSelectedProject] = useState<string | null>(null);
  const navigate = useNavigate();
  const [isNavigating, setIsNavigating] = useState(false);
  const [showProjectPopup, setShowProjectPopup] = useState(false);
  const styleRef = useRef<HTMLStyleElement | null>(null);

  // FIXED: Proper CSS injection with cleanup and no pointer-events blocking
  useEffect(() => {
    // Remove any existing style
    if (styleRef.current) {
      document.head.removeChild(styleRef.current);
    }

    // Add a style element for green submit button only (NO pointer-events manipulation)
    const style = document.createElement("style");
    style.textContent = `
      .green-submit-button button[type="submit"] {
        background-color: #22c55e !important;
        border-color: #22c55e !important;
      }
      .green-submit-button button[type="submit"]:hover:not(:disabled) {
        background-color: #16a34a !important;
        border-color: #16a34a !important;
      }
      .green-submit-button button[type="submit"]:disabled {
        background-color: #e5e7eb !important;
        border-color: #e5e7eb !important;
        color: #9ca3af !important;
      }
      /* REMOVED: All pointer-events: none rules that were causing the blocking */
    `;
    document.head.appendChild(style);
    styleRef.current = style;

    // CRITICAL: Cleanup function to remove the style
    return () => {
      if (styleRef.current && document.head.contains(styleRef.current)) {
        document.head.removeChild(styleRef.current);
        styleRef.current = null;
      }
    };
  }, []);

  // FIXED: Reset navigation state and cleanup on unmount
  useEffect(() => {
    return () => {
      setIsNavigating(false);
      // Force cleanup any CSS issues
      document.body.style.pointerEvents = "";
      document.body.classList.remove("navigation-in-progress");
    };
  }, []);

  // Handle project selection
  const handleProjectChange = useCallback((projectId: string | null) => {
    setSelectedProject(projectId);
    setShowProjectPopup(false);
  }, []);

  // FIXED: Handle project creation with proper state reset
  const handleProjectCreated = useCallback((project: any) => {
    // Reset any navigation states that might be blocking
    setIsNavigating(false);

    // Select the newly created project
    setSelectedProject(project.id.toString());
    setShowProjectPopup(false);
    console.log("Project created and selected:", project);

    // CRITICAL: Force cleanup any lingering CSS issues
    setTimeout(() => {
      document.body.style.pointerEvents = "";
      document.body.classList.remove("navigation-in-progress");

      // Remove any orphaned modal overlays
      document.querySelectorAll("[data-radix-portal]").forEach((portal) => {
        if (portal.children.length === 0) {
          portal.remove();
        }
      });
    }, 100);
  }, []);

  // Handle suggestion clicks - populate textarea instantly
  const handleSuggestionClick = useCallback((suggestion: string) => {
    setInput(suggestion);
  }, []);

  // FIXED: Simplified submit handler without pointer-events manipulation
  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      // Prevent multiple navigation attempts
      if (isNavigating) {
        return;
      }

      e.preventDefault();
      // Block if no project
      if (!selectedProject) {
        setShowProjectPopup(true);
        return;
      }

      const userMessage = input.trim();
      if (!userMessage) return;

      // Create conversation ID early
      const conversationId = crypto.randomUUID();

      // Set navigating state immediately
      setIsNavigating(true);

      try {
        // Clear input and navigate
        setInput("");

        // FIXED: Use immediate navigation without Promise wrapper
        navigate(`/chat/${conversationId}`, {
          replace: true,
          state: {
            initialMessage: userMessage,
            selectedProject: selectedProject,
          },
        });
      } catch (error) {
        console.error("Navigation error:", error);
        setIsNavigating(false);
      }
    },
    [isNavigating, input, navigate, selectedProject]
  );

  // FIXED: Add className only when actually navigating, without pointer-events blocking
  const containerClassName = isNavigating ? "opacity-75" : "";

  return (
    <div
      className={`flex flex-col h-full overflow-hidden bg-[#FCFCFC] ${containerClassName}`}
    >
      <div className="h-full flex flex-col items-center justify-center p-5">
        <div className="flex flex-col items-center mb-8 p-8">
          <h1 className="text-3xl font-medium text-gray-900 text-center mb-2">
            What can I help you build?
          </h1>
        </div>

        <div className="w-full max-w-[741px]">
          <ChatContainer className="h-auto">
            <div className="relative mb-3">
              <ChatForm isPending={isNavigating} handleSubmit={handleSubmit}>
                {({ files, setFiles }) => (
                  <div className="relative">
                    <MessageInput
                      placeholder="Ask me anything..."
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      allowAttachments={false}
                      isGenerating={isNavigating}
                      className="min-h-[120px] text-base rounded-2xl border-gray-200 hover:border-gray-300 focus-visible:border-gray-400 shadow-sm bg-white transition-all duration-200 p-4 pb-12 green-submit-button"
                      style={{ fontSize: "16px" }}
                    />
                  </div>
                )}
              </ChatForm>
              <Popover
                open={showProjectPopup}
                onOpenChange={(open) => {
                  // only allow closing the popover via outside click, ignore trigger opens
                  if (!open) setShowProjectPopup(false);
                }}
              >
                <PopoverTrigger asChild>
                  <div className="absolute bottom-3 left-3 z-10">
                    <ShadcnProjectSelector
                      selectedProject={selectedProject}
                      onProjectChange={handleProjectChange}
                      onProjectCreated={handleProjectCreated}
                    />
                  </div>
                </PopoverTrigger>
                <PopoverContent
                  side="top"
                  align="start"
                  className="w-auto p-3 bg-white border border-gray-200 shadow-lg rounded-lg"
                  sideOffset={8}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: STATUS_COLORS.ERROR }}
                    />
                    <span className="text-sm font-medium text-gray-900">
                      Please select a project to continue
                    </span>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </ChatContainer>

          <SuggestionCarousel
            suggestions={SUGGESTIONS}
            onSuggestionClick={handleSuggestionClick}
            isNavigating={isNavigating}
          />
        </div>
      </div>
    </div>
  );
};

// Use React.memo to prevent unnecessary re-renders of the entire component
export default React.memo(AILandingPage);
